/**
 * Document revisions — the part that actually solves the compatibility
 * problem.
 *
 * The rule the whole design rests on:
 *
 *   **Once a document revision is issued, never reconstruct its contents
 *   by executing current business logic.**
 *
 * A revision therefore stores the compiled, resolved IR alongside a
 * snapshot of the source that produced it. Viewing an old document replays
 * neither the form nor the block compiler; it renders persisted data. That
 * makes a block's implementation deletable once retention allows, instead
 * of something we carry forever in N backwards-compatible copies.
 *
 * Editing an issued revision is not an edit — it is a new revision.
 */
import { IR_VERSION } from '../ir/nodes.js';
export const REVISION_SCHEMA = 'regta.document.revision';
export const REVISION_VERSION = 1;
/**
 * Deterministic JSON: object keys sorted, no incidental whitespace. Two
 * compilations of the same inputs must produce the same string, or the
 * content hash means nothing.
 */
export function canonicalJson(value) {
    const walk = (input) => {
        if (Array.isArray(input))
            return input.map(walk);
        if (input && typeof input === 'object') {
            const source = input;
            const out = {};
            for (const key of Object.keys(source).sort()) {
                if (source[key] === undefined)
                    continue;
                out[key] = walk(source[key]);
            }
            return out;
        }
        return input;
    };
    return JSON.stringify(walk(value));
}
/** Hex sha-256 via WebCrypto — the same call works in Node and a browser. */
export async function sha256Hex(input) {
    const bytes = new TextEncoder().encode(input);
    const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes);
    return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}
export const hashDocument = (document) => sha256Hex(canonicalJson(document));
export async function createRevision(input) {
    return {
        schema: REVISION_SCHEMA,
        version: REVISION_VERSION,
        revisionId: input.revisionId,
        documentKey: input.documentKey,
        documentVersion: input.documentVersion,
        status: input.status ?? 'draft',
        compiledAt: input.compiledAt,
        irVersion: IR_VERSION,
        source: input.source,
        compilers: input.document.manifest,
        document: input.document,
        contentHash: await hashDocument(input.document),
        ...(input.artifacts ? { artifacts: input.artifacts } : {}),
    };
}
export const isFrozen = (revision) => revision.status !== 'draft';
/**
 * Guard for every recompile path. A frozen revision is superseded by a new
 * one; it is never regenerated in place, however tempting that looks when
 * a block is fixed.
 */
export function assertRecompilable(revision) {
    if (isFrozen(revision)) {
        throw new Error(`Revision ${revision.revisionId} is ${revision.status} and cannot be recompiled. Create a new revision instead.`);
    }
}
/** Verifies a stored revision still hashes to what it claims. */
export async function verifyRevision(revision) {
    return (await hashDocument(revision.document)) === revision.contentHash;
}
//# sourceMappingURL=revision.js.map